<script>
import MyuserAvatar from "./myuser-avatar.vue";
import IconTooltip from "../../../common/icon-tooltip.vue";
import User from "../../user/data/User";
import API from "../../../../API";
import MyuserFileUpload from "./myuser-file-upload.vue";
import ConfirmDialog from "../../../dialog/confirm-dialog.vue";
import DialogButton from "../../../dialog/components/dialog-button.vue";
import TextField from "../../../common/text-field.vue";
import SelectField from "../../../common/select-field.vue";
import toaster from "../../../common/toaster";
import PasswordFields from "../../../common/password-fields.vue";
import { publishNotifications, tagSearchOptions, timepickerModeOptions } from '../../user/data/variables';

const languages = Object.keys(Laravel.languages).map(key => ({value: key, text: Laravel.languages[key]}));

    export default {
        name: "myuser-card",
        components: {
            PasswordFields,
            SelectField, TextField, DialogButton, ConfirmDialog, MyuserFileUpload, IconTooltip, MyuserAvatar},
        props: {
            user: {
                type: User,
                required: true
            },
            disableEditImage: {
                type: Boolean,
                default: false
            },
            disableImage: {
                type: Boolean,
                default: false
            },
            disableEmail: {
                type: Boolean
            },
            invalidProperties: {
                type: Array,
                default: () => []
            },
            addMode: {
                type: Boolean
            }
        },
        data(vm) {
            return {
                passwordsMatch: true,
                showFileUploadDialog: false,
                showConfirmDeleteImageDialog: false,
                stringRules: fieldName => {
                    return [
                        function (value) {
                            if (value) {
                                return true;
                            }

                            return Lang.langGet('You must enter a ') + fieldName;
                        }
                    ];
                },
                emailRules: [
                    function (value) {
                        if (value) {
                            let isValidEmail = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value);

                            if (isValidEmail) return true;
                            return Lang.langGet('E-mail must be valid');
                        }

                        if (vm.user.isExternal) return true;
                        return Lang.langGet('You must enter an ') + 'Email';
                    }
                ],
                tConfirmDeleteImage: Lang.langGet('Do you really want to delete your profile image?'),
                tHint: Lang.langGet('Set whether the searched element must match ALL tags or at least a SINGLE tag'),
                tDeleteImage: Lang.langGet('Delete image'),
                tEditImage: Lang.langGet('Edit image'),
                tAddImage: Lang.langGet('Add image'),
                tFirstName: Lang.langGet('First Name'),
                tLastName: Lang.langGet('Last Name'),
                tGroupName: Lang.langGet('Group Name'),
                tExternalGroupName: Lang.langGet('External Group Name'),
                tEmail: Lang.langGet('Email Address'),
                tPublish: Lang.langGet('Publish Notifications'),
                tTagSearch: Lang.langGet('Tag Search'),
                tTimepickerMode: Lang.langGet('Timepicker Mode'),
                tLanguage: Lang.langGet('Language')
            }
        },
        computed: {
            languages() {
                return languages;
            },
            publishNotifications() {
                return publishNotifications
            },
            tagSearchOptions() {
                return tagSearchOptions
            },
            timepickerModeOptions() {
                return timepickerModeOptions;
            },
            disableEmailLogic() {
                if (this.disableEmail) return true;

                if (Laravel.isUserSuperAdmin) return false;
                if (this.addMode) return false;

                return !!(this.user.isExternal || this.user.isLdap);
            },
            isSuperadmin() {
                return Laravel.isUserSuperAdmin;
            }
        },
        methods: {
            show() {
                this.showDialog = true;
            },
            hide() {
                this.showDialog = false;
            },
            isValid(property, value) {
                if (property === 'firstName' || property === 'lastName') {
                    return !!value;
                }

                if (property === 'email') {
                    return /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/.test(value);
                }

                return true;
            },
            handleChange(property, value) {
                const valid = this.isValid(property, value);
                this.$emit('change', property, value, !valid);
            },
            handleSave(image) {
                this.$emit('save', image);
            },
            handleDeleteImage() {
                this.showConfirmDeleteImageDialog = true;
            },
            handleUploadImage() {
                this.showFileUploadDialog = true;
            },
            handlePasswordChange(type, value, isValid) {
                this.$emit('change', type, value, isValid);
            },
            handleCloseFileUploadDialog() {
                this.showFileUploadDialog = false;
            },
            handleAbortDeleteImage() {
                this.showConfirmDeleteImageDialog = false;
            },
            handleConfirmDeleteImage() {
                this.user.deletePicture();
                this.showConfirmDeleteImageDialog = false;
            },
        },
    }
</script>

<template>
    <div>
        <myuser-file-upload
            id="myuser-file-upload"
            :showDialog="showFileUploadDialog"
            :picture="user.profilePicture"
            @close="handleCloseFileUploadDialog"
            @save="handleSave"
        />
        <confirm-dialog v-if="showConfirmDeleteImageDialog"
            :message="tConfirmDeleteImage"
            :showDialog="showConfirmDeleteImageDialog"
            @cancel="handleAbortDeleteImage"
            @confirm="handleConfirmDeleteImage"
            :no-icons="true"
        />
        <div class="myuser-dialog-row justify-space-between">
            <div class="myuser-dialog-column align-center w-50 pa-2">
                <div v-if="!disableImage" class="myuser-dialog-row justify-space-between w-100">
                    <div class="d-flex flex-column align-center">
                        <myuser-avatar
                            :picture="user.profilePicture"
                            :userName="user.email"
                        />
                    </div>
                    <div class="myuser-dialog-column flex-column-reverse">
                        <dialog-button
                            class="w-100"
                            small
                            color="secondary"
                            @click="handleDeleteImage"
                            v-if="user.profilePicture && !disableEditImage"
                        >
                            <i class="mr-1 fa-light fa-trash fa-fw fa-lg" />
                            {{ tDeleteImage }}
                        </dialog-button>
                        <dialog-button
                            class="w-100 mb-1"
                            color="secondary"
                            small
                            @click="handleUploadImage"
                            v-if="!disableEditImage"
                        >
                            <i class="mr-1 fa-light fa-pencil fa-fw fa-lg"/>
                            {{ user.profilePicture ? tEditImage : tAddImage }}
                        </dialog-button>
                    </div>
                </div>
                <div v-else>
                    <div class="d-flex flex-column align-center">
                        <myuser-avatar />
                    </div>
                </div>
            </div>
            <div v-if="user.isLdap" class="myuser-dialog-column align-center w-50 pa-2">
                <text-field
                    autofocus
                    :disabled="!isSuperadmin"
                    @input="value => handleChange('lastName', value)"
                    :rules="stringRules(tGroupName)"
                    :value="user['lastName']"
                    :label="tGroupName"
                    :error="invalidProperties.some(property => property === 'lastName')"
                />
            </div>
            <div v-else-if="user.isExternal" class="myuser-dialog-column align-center w-50 pa-2">
                <text-field
                    autofocus
                    :disabled="!isSuperadmin"
                    @input="value => handleChange('lastName', value)"
                    :rules="stringRules(tExternalGroupName)"
                    :value="user['lastName']"
                    :label="tExternalGroupName"
                    :error="invalidProperties.some(property => property === 'lastName')"
                />
            </div>
            <div v-else class="myuser-dialog-column align-center w-50 pa-2">
                <text-field
                    v-if="!user.isExternal"
                    autofocus
                    @input="value => handleChange('firstName', value)"
                    :rules="stringRules(tFirstName)"
                    :value="user['firstName']"
                    :label="tFirstName"
                    :error="invalidProperties.some(property => property === 'firstName')"
                />
                <text-field
                    :disabled="user.isExternal"
                    @input="value => handleChange('lastName', value)"
                    :rules="stringRules(tLastName)"
                    :value="user['lastName']"
                    :label="tLastName"
                    :error="invalidProperties.some(property => property === 'lastName')"
                />
            </div>
        </div>
        <div class="myuser-dialog-row justify-space-between">
            <div class="myuser-dialog-column pa-2 w-50">
                <text-field
                    @input="value => handleChange('email', value)"
                    :rules="emailRules"
                    :disabled="disableEmailLogic"
                    :value="user.email"
                    :label="tEmail"
                    :autocomplete="'one-time-code'"
                    :error="invalidProperties.some(property => property === 'email')"
                />
            </div>
            <div class="myuser-dialog-column pa-2 w-50">
                <div class="myuser-dialog-row">
                    <select-field
                        :value="user.language"
                        @input="value => handleChange('language', value)"
                        :items="languages"
                        :label="tLanguage"
                    />
                </div>
            </div>
        </div>
        <div class="myuser-dialog-row">
            <div v-if="!user.isExternal || addMode" class="myuser-dialog-column w-50 pa-2">
                <div class="myuser-dialog-row">
                    <select-field
                        :value="user.suppressEmails"
                        @input="value => handleChange('suppressEmails', value)"
                        :items="publishNotifications"
                        :label="tPublish"
                    />
                </div>
            </div>
            <div class="myuser-dialog-column w-50 pa-2">
                <div class="myuser-dialog-row">
                    <select-field
                        :value="user.tagSearch"
                        @input="value => handleChange('tagSearch', value)"
                        :items="tagSearchOptions"
                        :label="tTagSearch"
                    />
                    <icon-tooltip
                        style="height: 60%"
                        :hintText="tHint"
                    />
                </div>
            </div>
        </div>
        <div class="myuser-dialog-row">
            <div class="myuser-dialog-column w-50 pa-2">
                <div class="myuser-dialog-row">
                    <select-field
                        :value="user.timepickerMode"
                        @input="value => handleChange('timepickerMode', value)"
                        :items="timepickerModeOptions"
                        :label="tTimepickerMode"
                    />
                </div>
            </div>
        </div>
        <div v-if="!user.isLdap && !user.isExternal" class="myuser-dialog-row">
            <password-fields
                :password="user.password"
                :passwordConfirmation="user.passwordConfirmation"
                @input="handlePasswordChange"
            />
        </div>
    </div>
</template>

<style scoped>
    .myuser-dialog-row {
        display: flex;
        flex-direction: row;
    }
    .myuser-dialog-column {
        display: flex;
        flex-direction: column;
    }
</style>
